import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { CheckCircle2, ChevronDown, Loader2, Save, X } from 'lucide-react'
import { Button } from '@/components/ui'
import { registerUnsavedChangesGuard } from '@/lib/unsavedChanges'
import {
  buildFinanceExpenseGroups,
  financeGroupTotal,
  sanitizeFinanceValues,
  type FinanceExpenseCategory,
  type FinanceMonthlyFactRow,
  type FinanceMonthlyFactValues,
} from '@/lib/financeMonthlyFacts'

const MONTHS = ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь']

function monthStart(year: number, month: number): string {
  return `${year}-${String(month).padStart(2, '0')}-01`
}

function formatEditableAmount(value: number): string {
  const hasKopecks = !Number.isInteger(value)
  return new Intl.NumberFormat('ru-RU', {
    minimumFractionDigits: hasKopecks ? 2 : 0,
    maximumFractionDigits: 2,
  }).format(value)
}

function formatCategoryTotal(value: number, currency: string): string {
  return new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency,
    minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(value)
}

export function MonthlyExpenseFactsEditor({
  year, rows, categories, currency, initialMonth, saving, error, onClose, onSave,
}: {
  year: number
  rows: FinanceMonthlyFactRow[]
  categories: FinanceExpenseCategory[]
  currency: string
  initialMonth: number
  saving: boolean
  error: string | null
  onClose: () => void
  onSave: (month: number, values: FinanceMonthlyFactValues, expectedVersion: number | null) => Promise<void>
}) {
  const [month, setMonth] = useState(initialMonth)
  const row = useMemo(() => rows.find(item => item.month_start === monthStart(year, month)) ?? null, [month, rows, year])
  const [draft, setDraft] = useState<Record<string, number | null>>({})
  const [fieldInputs, setFieldInputs] = useState<Record<string, string>>({})
  const [dirty, setDirty] = useState(false)
  const [closeConfirmationOpen, setCloseConfirmationOpen] = useState(false)
  const [pendingMonth, setPendingMonth] = useState<number | null>(null)
  const [savedAt, setSavedAt] = useState<Date | null>(null)
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(() => new Set())
  const panelRef = useRef<HTMLElement>(null)
  const expenseGroups = useMemo(() => buildFinanceExpenseGroups(
    categories,
    new Set(Object.keys(row?.values ?? {}))
  ), [categories, row])
  const allowedCodes = useMemo(() => new Set(expenseGroups.flatMap(group => group.fields.map(field => field.code))), [expenseGroups])

  useEffect(() => {
    const values = row?.values ?? {}
    setDraft(values)
    setFieldInputs(Object.fromEntries(Object.entries(values).map(([code, value]) => [code, String(value).replace('.', ',')])))
    setDirty(false)
  }, [row])

  useEffect(() => {
    setExpandedGroups(new Set())
  }, [month, year])

  useEffect(() => registerUnsavedChangesGuard(() => (
    !dirty || window.confirm('Есть несохранённые расходы. Выйти без сохранения?')
  )), [dirty])

  useEffect(() => {
    if (!dirty) return
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [dirty])

  const requestClose = useCallback(() => {
    if (saving) return
    if (dirty) {
      setCloseConfirmationOpen(true)
      return
    }
    onClose()
  }, [dirty, onClose, saving])

  useEffect(() => {
    const handleOutsidePointerDown = (event: PointerEvent) => {
      if (closeConfirmationOpen || pendingMonth !== null || panelRef.current?.contains(event.target as Node)) return
      event.preventDefault()
      event.stopPropagation()
      requestClose()
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || closeConfirmationOpen || pendingMonth !== null) return
      event.preventDefault()
      requestClose()
    }
    document.addEventListener('pointerdown', handleOutsidePointerDown, true)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('pointerdown', handleOutsidePointerDown, true)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [closeConfirmationOpen, pendingMonth, requestClose])

  const saveDraft = async (): Promise<boolean> => {
    try {
      await onSave(month, sanitizeFinanceValues(draft, allowedCodes), row?.version ?? null)
      setDirty(false)
      setSavedAt(new Date())
      return true
    } catch {
      return false
    }
  }

  const saveAndClose = async () => {
    if (await saveDraft()) onClose()
    else setCloseConfirmationOpen(false)
  }

  const saveAndChangeMonth = async () => {
    if (pendingMonth === null) return
    if (!await saveDraft()) {
      setPendingMonth(null)
      return
    }
    const nextMonth = pendingMonth
    setPendingMonth(null)
    setSavedAt(null)
    setMonth(nextMonth)
  }

  const changeMonth = (nextMonth: number) => {
    if (nextMonth === month) return
    if (dirty) {
      setPendingMonth(nextMonth)
      return
    }
    setSavedAt(null)
    setMonth(nextMonth)
  }

  const discardAndChangeMonth = () => {
    if (pendingMonth === null) return
    const nextMonth = pendingMonth
    setPendingMonth(null)
    setDirty(false)
    setSavedAt(null)
    setMonth(nextMonth)
  }

  const toggleGroup = (groupCode: string) => {
    setExpandedGroups(current => {
      const next = new Set(current)
      if (next.has(groupCode)) next.delete(groupCode)
      else next.add(groupCode)
      return next
    })
  }

  return createPortal(
    <div className="pointer-events-none fixed inset-0 z-[60] flex h-screen items-stretch justify-end [height:100dvh]">
      <section ref={panelRef} className="pointer-events-auto flex h-screen w-full flex-col border-l border-border bg-background shadow-2xl [height:100dvh] sm:max-w-xl">
        <header className="flex items-center justify-between gap-3 border-b border-border px-4 py-3">
          <div>
            <h3 className="font-semibold">Внести расходы</h3>
            <p className="text-xs text-muted-foreground">Одна карта на студию и месяц · {currency}</p>
          </div>
          <button type="button" onClick={requestClose} className="rounded p-2 hover:bg-muted" aria-label="Закрыть"><X size={18} /></button>
        </header>
        <div className="border-b border-border p-4">
          <select value={month} onChange={event => changeMonth(Number(event.target.value))} className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm">
            {MONTHS.map((label, index) => <option key={label} value={index + 1}>{label} {year}</option>)}
          </select>
          <p id="expense-amount-format-hint" className="mt-2 text-xs text-muted-foreground">
            Все суммы вводятся положительными — без знака минус. Можно указать до двух знаков после запятой или точки.
          </p>
        </div>
        <div className="flex-1 space-y-4 overflow-y-auto p-4">
          {expenseGroups.map(group => (
            <section key={group.code} className="overflow-hidden rounded-lg border border-border">
              <button
                type="button"
                onClick={() => toggleGroup(group.code)}
                className="flex w-full items-center justify-between gap-3 bg-muted/30 px-3 py-2.5 text-left text-sm font-semibold hover:bg-muted/60"
                aria-expanded={expandedGroups.has(group.code)}
              >
                <span>{group.label}</span>
                <span className="flex shrink-0 items-center gap-2">
                  <span className="font-medium tabular-nums text-muted-foreground">
                    {formatCategoryTotal(financeGroupTotal(group, sanitizeFinanceValues(draft, allowedCodes)), currency)}
                  </span>
                  <ChevronDown size={16} className={`transition-transform ${expandedGroups.has(group.code) ? 'rotate-180' : ''}`} />
                </span>
              </button>
              {expandedGroups.has(group.code) && <div className="divide-y divide-border">
                {group.fields.map(field => (
                  <label key={field.code} className="grid grid-cols-[minmax(0,1fr)_140px] items-center gap-3 px-3 py-2 text-sm">
                    <span>{field.label}{!field.isActive && <span className="ml-1 text-xs text-muted-foreground">(архивная статья)</span>}</span>
                    <input
                      inputMode="decimal"
                      type="text"
                      disabled={!field.isActive}
                      aria-describedby="expense-amount-format-hint"
                      value={fieldInputs[field.code] ?? ''}
                      onChange={event => {
                        const input = event.target.value.replace(/\s/g, '')
                        if (!/^\d*(?:[.,]\d{0,2})?$/.test(input)) return
                        const value = input === '' || input === '.' || input === ',' ? null : Number(input.replace(',', '.'))
                        setFieldInputs(current => ({ ...current, [field.code]: input }))
                        setDraft(current => ({ ...current, [field.code]: value }))
                        setDirty(true)
                      }}
                      onFocus={() => {
                        const value = draft[field.code]
                        if (value === null || value === undefined) return
                        setFieldInputs(current => ({ ...current, [field.code]: String(value).replace('.', ',') }))
                      }}
                      onBlur={() => {
                        const value = draft[field.code]
                        if (value === null || value === undefined) return
                        setFieldInputs(current => ({ ...current, [field.code]: formatEditableAmount(value) }))
                      }}
                      onWheel={event => event.currentTarget.blur()}
                      className="h-9 rounded-md border border-input bg-background px-3 text-right tabular-nums"
                    />
                  </label>
                ))}
              </div>}
            </section>
          ))}
          {error && <div className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}
        </div>
        <footer className="flex justify-end gap-2 border-t border-border p-3">
          <div className="mr-auto flex min-h-9 items-center text-xs text-emerald-600" aria-live="polite">
            {dirty
              ? <span className="text-amber-600">Есть несохранённые изменения</span>
              : savedAt && <><CheckCircle2 size={15} className="mr-1.5" />Сохранено в {savedAt.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}</>}
          </div>
          <Button size="sm" variant="outline" onClick={requestClose} disabled={saving}>Закрыть</Button>
          <Button size="sm" className="inline-flex items-center gap-2" onClick={() => void saveDraft()} disabled={saving || !dirty}>
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}Сохранить
          </Button>
        </footer>
      </section>

      {closeConfirmationOpen && (
        <div className="pointer-events-auto fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-xl border border-border bg-background p-5 shadow-xl">
            <h3 className="text-base font-semibold">Есть несохранённые изменения</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              Сохранить данные перед закрытием формы?
            </p>
            <div className="mt-5 flex flex-wrap justify-end gap-2">
              <Button type="button" size="sm" variant="outline" onClick={() => setCloseConfirmationOpen(false)}>
                Отмена
              </Button>
              <Button type="button" size="sm" variant="outline" onClick={onClose}>
                Выйти без сохранения
              </Button>
              <Button type="button" size="sm" className="inline-flex items-center gap-2" onClick={() => void saveAndClose()} disabled={saving}>
                {saving && <Loader2 size={16} className="animate-spin" />}Сохранить
              </Button>
            </div>
          </div>
        </div>
      )}

      {pendingMonth !== null && (
        <div className="pointer-events-auto fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-xl border border-border bg-background p-5 shadow-xl">
            <h3 className="text-base font-semibold">Есть несохранённые изменения</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              Сохранить расходы за «{MONTHS[month - 1]} {year}» перед переходом к месяцу «{MONTHS[pendingMonth - 1]} {year}»?
            </p>
            <div className="mt-5 flex flex-wrap justify-end gap-2">
              <Button type="button" size="sm" variant="outline" onClick={() => setPendingMonth(null)}>
                Отмена
              </Button>
              <Button type="button" size="sm" variant="outline" onClick={discardAndChangeMonth}>
                Перейти без сохранения
              </Button>
              <Button type="button" size="sm" className="inline-flex items-center gap-2" onClick={() => void saveAndChangeMonth()} disabled={saving}>
                {saving && <Loader2 size={16} className="animate-spin" />}Сохранить и перейти
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>,
    document.body,
  )
}
